import { useState } from 'react';
import { Zap } from 'lucide-react';

interface EnvelopeEditorProps {
  channelName: string;
}

export function EnvelopeEditor({ channelName }: EnvelopeEditorProps) {
  const [attack, setAttack] = useState(10);
  const [decay, setDecay] = useState(30);
  const [sustain, setSustain] = useState(70);
  const [release, setRelease] = useState(40);

  const calculatePath = () => {
    const height = 80;
    
    const attackX = (attack / 100) * 25;
    const decayX = attackX + (decay / 100) * 25;
    const sustainX = 70;
    const releaseX = 100;
    
    return `
      M 0,${height}
      L ${attackX},0
      L ${decayX},${height - (sustain / 100) * height}
      L ${sustainX},${height - (sustain / 100) * height}
      L ${releaseX},${height}
    `;
  };

  return (
    <div className="bg-[#252525] rounded p-3 border border-[#3a3a3a]">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={14} className="text-[#ff6b00]" />
        <span className="text-xs">ENVELOPE - {channelName}</span>
      </div>

      {/* Visual envelope display */}
      <div className="h-32 bg-[#1a1a1a] rounded mb-3 p-2 relative">
        <svg viewBox="0 0 100 80" className="w-full h-full" preserveAspectRatio="none">
          {/* Grid */}
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#2a2a2a" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100" height="80" fill="url(#grid)" />
          
          {/* Envelope curve */}
          <path
            d={calculatePath()}
            fill="none"
            stroke="#ff6b00"
            strokeWidth="1.5"
            strokeLinejoin="round"
            style={{ filter: 'drop-shadow(0 0 2px #ff6b00)' }}
          />
          
          {/* Stage labels */}
          <text x="12" y="75" fontSize="4" fill="#888">Attack</text>
          <text x="35" y="75" fontSize="4" fill="#888">Decay</text>
          <text x="60" y="75" fontSize="4" fill="#888">Sustain</text>
          <text x="85" y="75" fontSize="4" fill="#888">Release</text>
        </svg>
      </div>

      {/* ADSR Controls */}
      <div className="grid grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] opacity-70 mb-1 block">Attack</label>
          <input
            type="range"
            min="0"
            max="100"
            value={attack}
            onChange={(e) => setAttack(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-[10px] text-center mt-1 text-[#ff6b00]">{attack}ms</div>
        </div>
        <div>
          <label className="text-[10px] opacity-70 mb-1 block">Decay</label>
          <input
            type="range"
            min="0"
            max="100"
            value={decay}
            onChange={(e) => setDecay(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-[10px] text-center mt-1 text-[#ff6b00]">{decay}ms</div>
        </div>
        <div>
          <label className="text-[10px] opacity-70 mb-1 block">Sustain</label>
          <input
            type="range"
            min="0"
            max="100"
            value={sustain}
            onChange={(e) => setSustain(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-[10px] text-center mt-1 text-[#ff6b00]">{sustain}%</div>
        </div>
        <div>
          <label className="text-[10px] opacity-70 mb-1 block">Release</label>
          <input
            type="range"
            min="0"
            max="100"
            value={release}
            onChange={(e) => setRelease(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-[10px] text-center mt-1 text-[#ff6b00]">{release}ms</div>
        </div>
      </div>
    </div>
  );
}
